import { forwardRef, useId, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { FOCUS_FIELD } from '@/lib/focus';

/** 44px tall, 10px radius, 14/600 (5a). Focus takes a 1.5px ink border and the soft halo from the Design Book. */
export const inputClass = `w-full h-11 px-3 rounded-input border border-border bg-card text-[14px] font-semibold text-ink placeholder:text-ink-2 outline-none disabled:opacity-50 ${FOCUS_FIELD}`;

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { mono?: boolean }>(({ className, mono, ...props }, ref) => (
  <input ref={ref} className={cn(inputClass, mono && 'font-mono tabular-nums', className)} {...props} />
));
Input.displayName = 'Input';

/** Several lines in the same frame as `Input`. Height comes from `rows` or a class; it grows by dragging unless told not to. */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { mono?: boolean }>(({ className, mono, ...props }, ref) => (
  <textarea ref={ref} className={cn('w-full rounded-input border border-border bg-card px-3 py-[10px] text-[14px] font-semibold leading-[1.55] text-ink placeholder:text-ink-2 outline-none resize-y disabled:opacity-50', FOCUS_FIELD, mono && 'font-mono', className)} {...props} />
));
Textarea.displayName = 'Textarea';

/**
 * An icon button inside an input's right edge (the time field's clock, the date field's calendar). The wrapper
 * must be `relative` and the input needs `pr-9` so typing never runs under it.
 */
export const FieldButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(({ className, ...props }, ref) => (
  <button ref={ref} type="button" className={cn('absolute right-1 top-1/2 -translate-y-1/2 grid place-items-center size-8 rounded-[7px] text-ink-2 bg-transparent border-0 cursor-pointer hover:text-ink hover:bg-inset', className)} {...props} />
));
FieldButton.displayName = 'FieldButton';

/** Uppercase label, the control, then a hint or an error. Pass a render function to receive the generated id. */
export function Field({ label, hint, error, children, className }: { label: string; hint?: ReactNode; error?: string; children: (id: string) => ReactNode; className?: string }) {
  const id = useId();
  return (
    <div className={cn('grid gap-[6px]', className)}>
      <label htmlFor={id} className="text-[11px] font-extrabold uppercase tracking-[.06em] text-ink-2">{label}</label>
      {children(id)}
      {error ? <p className="text-meta-sm text-danger-fg m-0">{error}</p> : hint ? <p className="text-meta-sm text-ink-2 m-0">{hint}</p> : null}
    </div>
  );
}
